import React, { useMemo } from 'react';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import TableResizeHandle from './TableResizeHandle';
import groupSubtotalColumns from './groupSubtotalColumns';
import { offerTableScrollSx } from './offerTableScrollSx';
import { Model, finite, format, sum } from './presentationModel';
import useColumnOrder from './useColumnOrder';
import useScrollbarHeaderHeight from './useScrollbarHeaderHeight';
import useTableSizing from './useTableSizing';

type Offer = Model['displayedOfferRows'][number];
type Column = { id: string; label: string; numeric?: boolean; value: (row: Offer) => string | number | null };
type Props = {
  rows: Model['displayedOfferRows'];
  view: string;
  sort: string;
  direction: 'asc' | 'desc';
  page: number;
  onPage: (page: number) => void;
  onSort: (key: string) => void;
  onDetail: (row: Offer) => void;
};

/** Offers - Experience: TTV / Refunds / GM (Experience) on the refund basis, ICS cost, and NM = GM (Experience) − ICS. */
const EXPERIENCE_COLUMNS: Array<Column> = [
  { id: 'person', label: 'Pcm Name', value: (row) => row.pcm_name },
  { id: 'country', label: 'Offer Country', value: (row) => row.offer_country_name },
  { id: 'opportunity', label: 'Offer Opportunity', value: (row) => row.offer_opportunity_name },
  { id: 'offerName', label: 'Offer Name', value: (row) => row.offer_name },
  { id: 'product', label: 'Product', value: (row) => row.product_name },
  { id: 'adjustedTtv', label: 'TTV (Experience)', numeric: true, value: (row) => row.adjustedTtv },
  { id: 'refunds', label: 'Refunds (Experience)', numeric: true, value: (row) => row.refunds },
  { id: 'adjustedGm', label: 'GM (Experience)', numeric: true, value: (row) => row.adjustedGm },
  { id: 'ics', label: 'ICS Cost Margin Reduction', numeric: true, value: (row) => row.ics_cost_margin_reduction_aud },
  { id: 'netMargin', label: 'Net Margin Adjusted', numeric: true, value: (row) => row.experienceNetMargin },
];

export default function CommercialKpiOffers({ rows, view, sort, direction, page, onPage, onSort, onDetail }: Props) {
  const scrollRef = useScrollbarHeaderHeight();
  const { tableRef, columnWidths, narrowedColumns, resizeAxis, startResize, resizeWithKeyboard } = useTableSizing();
  const columns: Array<Column> =
    view === 'Experience'
      ? EXPERIENCE_COLUMNS
      : [
          { id: 'person', label: 'Pcm Name', value: (row) => row.pcm_name },
          ...(view === 'MM' ? [{ id: 'region', label: 'Pcm Region', value: (row: Offer) => row.pcm_region }] : []),
          { id: 'country', label: 'Offer Country', value: (row) => row.offer_country_name },
          { id: 'opportunity', label: 'Offer Opportunity', value: (row) => row.displayName },
          ...(view === 'PCM' ? [{ id: 'status', label: 'Offer Status', value: (row: Offer) => row.status }] : []),
          { id: 'product', label: 'Product', value: (row) => row.displayProduct },
          ...(view === 'PCM'
            ? [
                {
                  id: 'marginForecast',
                  label: 'Margin Forecast',
                  numeric: true,
                  value: (row: Offer) => row.marginForecast,
                },
              ]
            : []),
          { id: 'ttv', label: 'TTV', numeric: true, value: (row) => row.ttv },
          { id: 'adjustedTtv', label: 'TTV (Excl. Experience)', numeric: true, value: (row) => row.adjustedTtv },
          {
            id: 'refunds',
            label: 'Refunds (Excl. Experiences)',
            numeric: true,
            value: (row) => row.refunds,
          },
          { id: 'gm', label: 'GM', numeric: true, value: (row) => row.gm },
          { id: 'adjustedGm', label: 'GM (Excl. Experience)', numeric: true, value: (row) => row.adjustedGm },
          ...(view === 'PCM'
            ? [
                {
                  id: 'lpAddback',
                  label: 'Lux+ Total Margin Addback (AR)',
                  numeric: true,
                  value: (row: Offer) => row.lp_margin_add_back_ar,
                },
              ]
            : []),
          { id: 'promo', label: 'Promo Used Aud', numeric: true, value: (row) => row.promo_used_aud },
          ...(view === 'PCM'
            ? [
                {
                  id: 'tourAddback',
                  label: 'PCM Tour Addback',
                  numeric: true,
                  value: (row: Offer) => row.pcm_tour_addback,
                },
                {
                  id: 'ics',
                  label: 'ICS Cost Margin Reduction',
                  numeric: true,
                  value: (row: Offer) => row.ics_cost_margin_reduction_aud,
                },
                {
                  id: 'tourReduction',
                  label: 'Ten Percent PCM Tour Margin Reduction',
                  numeric: true,
                  value: (row: Offer) => row.ten_percent_pcm_tour_margin_reduction,
                },
                {
                  id: 'netMargin',
                  label: 'Net Margin Adjusted',
                  numeric: true,
                  value: (row: Offer) => row.net_margin_adjusted,
                },
              ]
            : []),
        ];
  const allColumns: Array<Column> = [
    ...(view === 'PCM' ? [{ id: 'rowNumber', label: '#', value: () => null }] : []),
    ...columns,
  ];
  const { order, columnDragProps } = useColumnOrder(allColumns.map((column) => column.id));
  const visibleColumns = order.map((id) => allColumns.find((column) => column.id === id)).filter(Boolean);
  const columnIds = visibleColumns.map((column) => column.id);
  const fixedLayout = columnIds.every((id) => columnWidths[id] !== undefined);
  const sorted = useMemo(() => {
    if (sort === 'source') {
      const owners = new Map<string | null, Array<Offer>>();
      rows.forEach((row) => {
        const key = row.pcm_name;
        const list = owners.get(key) ?? [];
        list.push(row);
        owners.set(key, list);
      });
      const totals = new Map([...owners.entries()].map(([owner, offers]) => [owner, sum(offers, (row) => row.gm_ar)]));
      return [...rows].sort((a, b) => {
        const an = a.pcm_name;
        const bn = b.pcm_name;
        const av = totals.get(an);
        const bv = totals.get(bn);
        const ownerOrder = an === null ? 1 : bn === null ? -1 : an.localeCompare(bn);
        if (an !== bn) {
          return finite(av) && finite(bv) ? bv - av || ownerOrder : finite(av) ? -1 : finite(bv) ? 1 : ownerOrder;
        }
        return [
          view === 'MM' ? a.pcm_region : '',
          a.offer_country_name,
          a.displayName,
          view === 'PCM' ? a.status : '',
          a.displayProduct,
        ]
          .join('\u0000')
          .localeCompare(
            [
              view === 'MM' ? b.pcm_region : '',
              b.offer_country_name,
              b.displayName,
              view === 'PCM' ? b.status : '',
              b.displayProduct,
            ].join('\u0000'),
          );
      });
    }
    const column = columns.find((item) => item.id === sort);
    if (!column) {
      return rows;
    }
    return [...rows].sort((a, b) => {
      const av = column.value(a);
      const bv = column.value(b);
      if (av === null || av === undefined) {
        return bv === null || bv === undefined ? 0 : 1;
      }
      if (bv === null || bv === undefined) {
        return -1;
      }
      const value = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return direction === 'asc' ? value : -value;
    });
    // Column definitions use only view and source fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, view, sort, direction]);
  const safePage = Math.min(Math.max(0, page), Math.max(0, Math.ceil(sorted.length / 50) - 1));
  function subtotal(label: string, group: Array<Offer>, id: string, grand = false) {
    let labelShown = false;
    return (
      <TableRow
        key={id}
        sx={{
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#0F0C08' : '#F3F1EA'),
          '& .MuiTableCell-root': { fontWeight: 600, borderTop: 1, borderColor: 'divider' },
        }}
      >
        {groupSubtotalColumns(visibleColumns, (column) =>
          Boolean(column.numeric && column.id !== 'marginForecast'),
        ).map(({ measure, columns: groupColumns }) => {
          const column = groupColumns[0];
          const text = !measure && !labelShown ? (grand ? label : `${label} · Subtotal`) : null;
          if (!measure) labelShown = true;
          return (
            <TableCell key={column.id} colSpan={groupColumns.length} align={measure ? 'right' : 'left'}>
              {measure
                ? format(
                    sum(group, (row) => {
                      const value = column.value(row);
                      return finite(value) ? value : null;
                    }),
                    'money',
                  )
                : text}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }
  return (
    <>
      <TableContainer ref={scrollRef} sx={offerTableScrollSx}>
        <Table
          ref={tableRef}
          stickyHeader
          size="small"
          sx={{
            ...(fixedLayout
              ? {
                  tableLayout: 'fixed',
                  width: columnIds.reduce((total, id) => total + columnWidths[id], 0),
                  minWidth: 0,
                }
              : { minWidth: view === 'PCM' ? 2200 : 1800 }),
            '& .MuiTableCell-root': { py: 0.75, px: 1, fontSize: 12, lineHeight: 1.45 },
            ...(fixedLayout ? { '& .MuiTableCell-body': { overflowWrap: 'anywhere' } } : {}),
            ...(resizeAxis ? { '&, & *': { cursor: 'col-resize' } } : {}),
            '& .MuiTableCell-head': {
              height: 32,
              bgcolor: 'primary.main',
              color: '#FFFFFF',
            },
            '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-icon': {
              color: '#FFFFFF !important',
            },
          }}
          aria-label={`${view} offer details`}
        >
          {fixedLayout && (
            <Box component="colgroup">
              {columnIds.map((id) => (
                <Box component="col" key={id} sx={{ width: columnWidths[id] }} />
              ))}
            </Box>
          )}
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  {...columnDragProps(column.id)}
                  data-column-id={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  sx={{
                    position: 'sticky',
                    overflow: 'hidden',
                    whiteSpace: narrowedColumns[column.id] ? 'normal' : 'nowrap',
                  }}
                >
                  {column.id === 'rowNumber' ? (
                    '#'
                  ) : (
                    <TableSortLabel
                      active={sort === column.id}
                      direction={sort === column.id ? direction : 'asc'}
                      onClick={() => onSort(column.id)}
                      sx={narrowedColumns[column.id] ? { maxWidth: '100%' } : undefined}
                    >
                      <Box
                        component="span"
                        sx={narrowedColumns[column.id] ? { minWidth: 0, overflowWrap: 'anywhere' } : undefined}
                      >
                        {column.label}
                      </Box>
                    </TableSortLabel>
                  )}
                  <TableResizeHandle
                    axis="column"
                    label={`Resize ${column.label} column`}
                    size={columnWidths[column.id]}
                    onPointerDown={(event) => startResize(event, 'column', column.id)}
                    onKeyDown={(event) => resizeWithKeyboard(event, 'column', column.id)}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Workbook Offers sheets lead with a Grand Total across every displayed row, not just this page. */}
            {sorted.length > 0 && subtotal('Grand Total', rows, 'grand-total', true)}
            {sorted.slice(safePage * 50, safePage * 50 + 50).map((offer, index) => {
              const next = sorted[safePage * 50 + index + 1];
              const ownerEnd = next?.pcm_name !== offer.pcm_name;
              const regionEnd = ownerEnd || next.pcm_region !== offer.pcm_region;
              return (
                <React.Fragment key={offer.key}>
                  <TableRow key={offer.key} hover onClick={() => onDetail(offer)} sx={{ cursor: 'pointer' }}>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.numeric ? 'right' : 'left'}
                        sx={column.id === 'opportunity' && !fixedLayout ? { minWidth: 180, maxWidth: 300 } : undefined}
                      >
                        {column.id === 'rowNumber' ? (
                          safePage * 50 + index + 1
                        ) : column.id === 'opportunity' ? (
                          <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: 'inherit', textAlign: 'left' }}>
                            {offer.displayName}
                          </Button>
                        ) : column.numeric ? (
                          format(column.value(offer) as number | null, 'money')
                        ) : (
                          (column.value(offer) ?? 'Unknown')
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {sort === 'source' &&
                    view === 'MM' &&
                    regionEnd &&
                    subtotal(
                      `${offer.pcm_name ?? 'Unknown'} · ${offer.pcm_region ?? 'Unknown'}`,
                      rows.filter((row) => row.pcm_name === offer.pcm_name && row.pcm_region === offer.pcm_region),
                      `region-${offer.key}`,
                    )}
                  {sort === 'source' &&
                    ownerEnd &&
                    subtotal(
                      offer.pcm_name ?? 'Unknown',
                      rows.filter((row) => row.pcm_name === offer.pcm_name),
                      `owner-${offer.key}`,
                    )}
                </React.Fragment>
              );
            })}
            {!sorted.length && (
              <TableRow>
                <TableCell colSpan={columns.length + (view === 'PCM' ? 1 : 0)}>
                  No source offer rows match the filters and selected owner/region tuples.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sorted.length}
        page={safePage}
        rowsPerPage={50}
        rowsPerPageOptions={[50]}
        onPageChange={(_, next) => onPage(next)}
        sx={{
          flexShrink: 0,
          '.MuiTablePagination-toolbar': { minHeight: 32, px: 0 },
          '.MuiTablePagination-spacer': { flex: 0 },
        }}
      />
    </>
  );
}
